/**
 * NodeContextMenu - Menú contextual para nodos de la red canónica
 *
 * @version 1.0
 * @created 2026-03-16
 * @module core/spatial/components
 *
 * @description
 * Menú contextual que aparece al hacer click derecho sobre un nodo.
 * Ofrece opciones de extender red desde nodo o eliminar nodo.
 */

import { useEffect, useRef } from 'react';
import { Trash2, GitBranch } from 'lucide-react';
import { mapEngine } from '../../map-engine/MapEngineService';
import './node-context-menu.css';

export interface NodeContextMenuProps {
  /** Coordenadas [lng, lat] donde mostrar el menú */
  position: [number, number];
  /** ID del nodo seleccionado */
  nodeId: string;
  /** Callback al seleccionar "Extender red desde nodo" */
  onExtendNetwork: () => void;
  /** Callback al seleccionar "Eliminar nodo" */
  onDelete: () => void;
  /** Callback al cerrar el menú */
  onClose: () => void;
}

/**
 * Componente NodeContextMenu
 */
export function NodeContextMenu({
  position,
  nodeId,
  onExtendNetwork,
  onDelete,
  onClose,
}: NodeContextMenuProps): JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Posicionar menú en coordenadas del mapa
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !menuRef.current) return;

    const updatePosition = (): void => {
      if (!map || !menuRef.current) return;

      const point = map.project([position[0], position[1]]);

      // Offset del TopBar
      const uiScale = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale')) || 1.0;
      const topBarHeight = 120 * uiScale;

      const adjustedY = point.y + topBarHeight;

      // Posicionar ligeramente arriba y a la derecha del click
      menuRef.current.style.left = `${point.x + 8}px`;
      menuRef.current.style.top = `${adjustedY - 8}px`;
    };

    updatePosition();

    map.on('move', updatePosition);
    map.on('zoom', updatePosition);

    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [position]);

  /**
   * Cerrar con ESC
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  /**
   * Cerrar al hacer click fuera
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Pequeño delay para no capturar el click que abrió el menú
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 50);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div ref={menuRef} className="node-context-menu">
      <button
        className="node-context-menu-item"
        onClick={() => {
          onExtendNetwork();
        }}
      >
        <GitBranch size={16} />
        <span>Extender red desde nodo</span>
      </button>
      <div className="node-context-menu-divider" />
      <button
        className="node-context-menu-item node-context-menu-item--danger"
        onClick={() => {
          onDelete();
        }}
      >
        <Trash2 size={16} />
        <span>Eliminar nodo</span>
      </button>
    </div>
  );
}
